// แท็บ "วันนี้" — คำนวณงานประจำวันจากตู้ทั้งหมด (pure, ไม่แตะ DB)
// อินพุต: vms = ships.map(toRailVM) (ทรง live), certs = certificate_of_origin ทุก consignment
// เอาต์พุต: { urgent, tasks, calendar, backlog, counts } ให้หน้า Today วาด
import { daysUntil } from './urgency.js';
import { reqLabel, STATUS_FLOW } from './reqtypes.js';

// "ยังไม่ปิดจริง" สำหรับมุมมองงานประจำวัน — 'delivered' ยังถือว่าต้องตรวจรับเข้าคลัง
// (isDone ของแอพนับ delivered ว่าจบ แต่แท็บนี้ต้องดันให้ปิดงานให้ครบถึง 'received')
const isOpen = (s) => s !== 'received' && s !== 'void';

const FORM_LABEL = {
  D: 'Form D (อาเซียน)', E: 'Form E (จีน)', AJ_JTEPA: 'JTEPA (ญี่ปุ่น)',
  AANZ: 'AANZ', AK: 'Form AK (เกาหลี)', AI: 'Form AI (อินเดีย)', RCEP: 'RCEP',
  other: 'Form สิทธิ', none: 'CO',
};
const formLabel = (t) => FORM_LABEL[t] ?? 'Form สิทธิ';

const statusIdx = (s) => { const i = STATUS_FLOW.indexOf(s); return i < 0 ? 0 : i; };
const IDX_CUSTOMS = STATUS_FLOW.indexOf('customs_submitted');
const IDX_IN_TRANSIT = STATUS_FLOW.indexOf('in_transit');

const BLOCKING = ['before_ship', 'before_pickup', 'before_clear'];

// นับวันแบบข้อความไทย (เอาไว้บอก "ถึงในกี่วัน / เลยมากี่วัน")
export function clockText(dateStr, { arrivedWord = 'ถึงแล้ว', label = 'ถึงใน' } = {}) {
  if (!dateStr) return 'ยังไม่กำหนดวัน';
  const d = daysUntil(dateStr);
  if (d < 0) return `${arrivedWord} ${-d} วัน`;
  if (d === 0) return `${label}วันนี้`;
  return `${label} ${d} วัน`;
}

// requirement ที่ยังขาดและ "บล็อก" (ต้องได้ก่อนขั้นตอนถัดไป) — ไม่รวม CO (แยกไปกลุ่มยืนยัน Form)
const blockingGaps = (reqs) => (reqs ?? []).filter(r =>
  r.required && ['missing', 'flagged'].includes(r.status) &&
  BLOCKING.includes(r.needed_by) && r.req_type !== 'certificate_of_origin');

const supplierNames = (vm) => [...new Set((vm.consignments ?? []).map(c => c.supplier_name).filter(Boolean))];
const supplierText = (vm) => supplierNames(vm).join(' · ') || vm.displayName || vm.id;

// ── งานประจำวัน 4 กลุ่ม (แต่ละงานผูกกับตู้ + บอกซัพพลายเออร์ + กำหนดเวลา) ──
function buildTasks(active, certById) {
  const verifyCO = [], exchangeDO = [], chaseDocs = [], receiveClose = [];

  for (const vm of active) {
    const reqs = vm.requirements ?? [];
    const eta = vm.raw?.eta;
    const dEta = daysUntil(eta);
    const idx = statusIdx(vm.raw?.status);

    // 1) ยืนยัน Form/CO — มี CO ที่ required แต่ certificate ยังไม่ verified
    for (const c of (vm.consignments ?? [])) {
      const coReq = reqs.find(r => r.consignment_id === c.id &&
        r.req_type === 'certificate_of_origin' && r.required);
      if (!coReq) continue;
      const cert = certById[c.id];
      if (cert?.verified) continue;
      const hasDoc = coReq.status === 'received' || coReq.status === 'verified';
      const self = vm.raw?.clearance === 'self';
      verifyCO.push({
        vm, consignmentId: c.id, clock: vm.clock,
        title: hasDoc ? `ยืนยัน ${formLabel(cert?.form_type)} ให้ ${c.supplier_name}`
                      : `ตาม ${formLabel(cert?.form_type)} จาก ${c.supplier_name} แล้วยืนยัน`,
        sub: `${vm.id} · ${hasDoc ? 'เอกสารมาแล้ว รอตรวจ/ยืนยัน' : 'ยังไม่ได้เอกสาร (ออกย้อนหลังไม่ได้)'}`
          + ` · ${self ? 'เดินพิธีเอง — ต้องผ่านก่อนยื่น' : 'มี broker ช่วยตรวจอีกชั้น'} · ${clockText(eta)}`,
        ready: hasDoc,
      });
    }

    // 2) แลก D/O + เตรียมรับของ — เฉพาะตู้ "เรือ" (air ใช้ AWB ไม่มี D/O),
    //    ส่งของแล้วแต่ยังไม่ยื่นพิธี และของถึง/ใกล้ถึง (≤3 วัน). ต้องมี B/L ต้นฉบับก่อนถึงแลกได้
    if (vm.mode === 'sea' && idx >= IDX_IN_TRANSIT && idx < IDX_CUSTOMS &&
        Number.isFinite(dEta) && dEta <= 3) {
      // ตู้รวม (หลาย consignment) มี B/L หลายใบ — ต้องได้ครบทุกใบถึงแลก D/O ได้ (ว่าง = ถือว่าครบ)
      const blReqs = reqs.filter(r => r.req_type === 'bl' && r.required);
      const haveBl = blReqs.every(r => ['received', 'verified'].includes(r.status));
      exchangeDO.push({
        vm, clock: vm.clock,
        title: haveBl ? `แลก D/O — ของ ${supplierText(vm)}`
                      : `ตาม B/L ต้นฉบับก่อน — ของ ${supplierText(vm)}`,
        sub: haveBl
          ? `${vm.id} · แลก D/O กับ ${vm.raw?.forwarder || 'สายเรือ/ตัวแทน'} · ${clockText(eta)}`
          : `${vm.id} · ยังไม่มี B/L ต้นฉบับ — แลก D/O ไม่ได้จนกว่าจะได้ · ${clockText(eta)}`,
        ready: haveBl,
      });
    }

    // 3) ตามเอกสารที่ยังขาด (บล็อก) — เฉพาะตู้ที่ใกล้ถึง (clock ≤ 7)
    if (Number.isFinite(vm.clock) && vm.clock <= 7) {
      const gaps = blockingGaps(reqs);
      if (gaps.length) {
        const first = reqLabel(gaps[0].req_type);
        chaseDocs.push({
          vm, clock: vm.clock,
          title: gaps.length > 1 ? `ตาม ${first} + อีก ${gaps.length - 1} ใบ` : `ตาม ${first}`,
          sub: `${vm.id} · ${supplierText(vm)} · ${clockText(eta)}`,
          count: gaps.length,
        });
      }
    }

    // 4) รับของเข้าคลัง + ปิดงาน — ผ่านพิธีแล้ว/ของถึงแล้ว ยังไม่ปิด
    const goodsReq = reqs.find(r => r.req_type === 'goods_receipt' && r.required);
    const goodsPending = !goodsReq || ['missing', 'flagged'].includes(goodsReq.status);
    if (Number.isFinite(dEta) && dEta <= 0 && goodsPending && idx >= IDX_IN_TRANSIT) {
      receiveClose.push({
        vm, clock: vm.clock,
        title: `รับของเข้าคลัง แล้วปิดงาน — ${supplierText(vm)}`,
        sub: `${vm.id} · ${clockText(eta)} · ${idx >= IDX_CUSTOMS ? 'ผ่านพิธีแล้ว' : 'รอเดินพิธี/ตรวจปล่อย'}`,
      });
    }
  }

  const byClock = (a, b) => (a.clock ?? 1e9) - (b.clock ?? 1e9);
  return [
    { key: 'verifyCO', title: 'ยืนยัน Form สิทธิ (CO) วันนี้', hint: 'Form สิทธิต้องยืนยันก่อนเดินพิธี ออกย้อนหลังไม่ได้', items: verifyCO.sort(byClock) },
    { key: 'exchangeDO', title: 'แลก D/O / เตรียมรับของ', hint: 'ของถึง/ใกล้ถึง — แลกใบสั่งปล่อยกับสายเรือหรือตัวแทน', items: exchangeDO.sort(byClock) },
    { key: 'chaseDocs', title: 'ตามเอกสารที่ยังขาด', hint: 'เอกสารที่ต้องได้ก่อนขั้นตอนถัดไป (รวมใบขน/อากร) ของตู้ที่ใกล้ถึง', items: chaseDocs.sort(byClock) },
    { key: 'receiveClose', title: 'รับของเข้าคลัง + ปิดงาน', hint: 'ของถึงแล้ว — ตรวจรับเข้าคลังแล้วปิดงาน', items: receiveClose.sort(byClock) },
  ];
}

// ── แถบเร่งด่วนวันนี้ — ตู้ที่ "ร้อน" ที่สุด (storage เสี่ยง / ของถึงแล้วยังไม่ปิด / urgent) ──
function buildUrgent(active) {
  const out = [];
  for (const vm of active) {
    const eta = vm.raw?.eta;
    const ft = vm.raw?.free_time_ends;
    const dEta = daysUntil(eta);
    const dFt = daysUntil(ft);
    const gaps = blockingGaps(vm.requirements);
    let reason = null, sev = 0;
    if (Number.isFinite(dFt) && dFt <= 3 && (gaps.length || vm.urgency === 'urgent')) {
      const rate = vm.raw?.storage_per_day ? ` ก่อนเสีย storage ฿${Number(vm.raw.storage_per_day).toLocaleString()}/วัน` : '';
      reason = `Free time เหลือ ${dFt < 0 ? 0 : dFt} วัน${rate}${gaps.length ? ` — ยังขาดเอกสาร ${gaps.length} ใบ` : ''}`;
      sev = 3;
    } else if (Number.isFinite(dEta) && dEta < 0 && isOpen(vm.raw?.status)) {
      reason = `ของถึงแล้ว ${-dEta} วัน ยังไม่ปิดงาน — ${vm.why}`;
      sev = 2;
    } else if (vm.urgency === 'urgent') {
      reason = vm.why;
      sev = 1;
    }
    if (reason) out.push({ vm, reason, sev, clock: vm.clock });
  }
  return out.sort((a, b) => (b.sev - a.sev) || ((a.clock ?? 1e9) - (b.clock ?? 1e9)));
}

// ── ปฏิทิน — เหตุการณ์ราย "YYYY-MM-DD": ของถึง (eta) · ออกต้นทาง (etd) · หมด free time ──
function buildCalendar(vms) {
  const map = {};
  const push = (dateStr, ev) => {
    if (!dateStr) return;
    const key = String(dateStr).slice(0, 10);
    (map[key] ??= []).push(ev);
  };
  for (const vm of vms) {
    const done = !isOpen(vm.raw?.status);
    push(vm.raw?.eta, { code: vm.id, vm, kind: 'eta', urgency: done ? 'done' : vm.urgency });
    push(vm.raw?.etd, { code: vm.id, vm, kind: 'etd', urgency: done ? 'done' : 'ok' });
    if (!done) push(vm.raw?.free_time_ends, { code: vm.id, vm, kind: 'free', urgency: 'urgent' });
  }
  return map;
}

// วันที่มี "ความเคลื่อนไหว" ล่าสุดของตู้ = ใหม่สุดใน updated_at ของตู้ + ของ requirement ทุกใบ
function lastActivityMs(vm) {
  let ms = vm.raw?.updated_at ? new Date(vm.raw.updated_at).getTime() : 0;
  for (const r of (vm.requirements ?? [])) {
    if (r.updated_at) ms = Math.max(ms, new Date(r.updated_at).getTime());
  }
  return ms || null;
}
const hasOpenGap = (reqs) => (reqs ?? []).some(r =>
  r.required && ['missing', 'flagged'].includes(r.status));

// ── ตู้ค้างนาน / ตู้เก่า — สองแบบ: (A) ของถึงเกิน 3 วันยังไม่ผ่านพิธี, (B) นิ่งไม่ขยับ ≥14 วัน ──
const IDLE_DAYS = 14;
const OVERDUE_DAYS = 3;
const IDX_DUTY_PAID = STATUS_FLOW.indexOf('duty_paid');
function buildBacklog(active, now) {
  const nowMs = now.getTime();
  const rows = [];
  for (const vm of active) {
    const dEta = daysUntil(vm.raw?.eta);
    const dFt = daysUntil(vm.raw?.free_time_ends);
    const dEtd = daysUntil(vm.raw?.etd);
    const idx = statusIdx(vm.raw?.status);
    const cleared = idx >= IDX_DUTY_PAID;
    const lastMs = lastActivityMs(vm);
    const idleDays = lastMs ? Math.floor((nowMs - lastMs) / 86400000) : null;

    // จัดลำดับตามความเสี่ยง: เงินไหล > ของถึงค้าง > ตกค้างต้นทาง > เงียบนาน
    let tier = 0, reason = null;
    if (Number.isFinite(dFt) && dFt < 0 && !cleared && hasOpenGap(vm.requirements)) {
      // (A) storage เดินแล้ว — free time หมด ยังไม่ผ่านพิธี + ยังขาดเอกสาร (เสียเงินจริง)
      const rate = vm.raw?.storage_per_day ? ` ~฿${Number(vm.raw.storage_per_day).toLocaleString()}/วัน` : '';
      tier = 4; reason = `หมด free time ${-dFt} วัน — ค่าเก็บเดินแล้ว${rate} (${vm.phase})`;
    } else if (Number.isFinite(dEta) && dEta <= -OVERDUE_DAYS && !cleared) {
      // (B) ของถึงเกิน 3 วัน แต่ยังไม่ผ่านพิธี/ยังไม่ปิด
      tier = 3; reason = `ของถึงแล้ว ${-dEta} วัน ยังไม่ผ่านพิธี/ยังไม่ปิด (${vm.phase})`;
    } else if (Number.isFinite(dEtd) && dEtd < 0 && idx < IDX_IN_TRANSIT) {
      // (C) เลยวันออกจากต้นทางแล้ว แต่ยังไม่ขึ้นเรือ/เครื่อง (ตกค้างต้นทาง)
      tier = 2; reason = `เลยวันออกจากต้นทาง ${-dEtd} วัน ยังไม่ออก (${vm.phase})`;
    } else if (idleDays != null && idleDays >= IDLE_DAYS && hasOpenGap(vm.requirements) && vm.urgency === 'ok') {
      // (D) เงียบไม่ขยับนาน + ยังมีงานค้าง (ตู้ที่ถูกลืม)
      tier = 1; reason = `เงียบมา ${idleDays} วัน ยังมีเอกสารค้าง (${vm.phase})`;
    }
    if (!reason) continue;

    // score: tier สูงก่อน แล้วในกลุ่มเดียวกันเรียงตามความเก่า (eta ติดลบมากสุด / idle มากสุด)
    const within = Number.isFinite(dEta) && dEta < 0 ? -dEta : (idleDays ?? 0);
    rows.push({ vm, idleDays, tier, staleScore: tier * 1e6 + within, reason });
  }
  return rows.sort((a, b) => b.staleScore - a.staleScore);
}

// ── งานของฉัน/เตือนความจำ → ปฏิทิน (เฉพาะที่มีวันกำหนดและยังไม่เสร็จ) ──
export function reminderCalendar(reminders = []) {
  const map = {};
  for (const r of reminders) {
    if (!r.due_date || r.done) continue;
    const key = String(r.due_date).slice(0, 10);
    (map[key] ??= []).push({ kind: 'reminder', reminder: r });
  }
  return map;
}
export function mergeCalendars(...maps) {
  const out = {};
  for (const m of maps) for (const k of Object.keys(m || {})) (out[k] ??= []).push(...(m[k] || []));
  return out;
}

// ── ข้อมูลตัวอย่างสำหรับโหมด MOCK (dev) — ป้อน vm สังเคราะห์เข้า buildToday ตัวเดียวกัน
//    เพื่อพรีวิว UI เต็มรูปแบบและใช้ตรรกะจริง (ไม่ใช่ข้อมูลจำลองแยกชุด) ──
export function buildTodayMock(now = new Date()) {
  const p2 = (n) => String(n).padStart(2, '0');
  // สร้างวันแบบ local (ไม่ใช้ toISOString ที่แปลงเป็น UTC แล้วเลื่อนวันตอนตี 1-7 โมงไทย)
  const day = (off) => { const d = new Date(now); d.setDate(d.getDate() + off); return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`; };
  const iso = (off) => { const d = new Date(now); d.setDate(d.getDate() + off); return d.toISOString(); };
  let rid = 0;
  const R = (consignment_id, req_type, status, needed_by, required = true) =>
    ({ id: 'r' + (++rid), consignment_id, req_type, status, needed_by, required, updated_at: iso(-2) });
  const clockOf = (eta, ft) => Math.min(daysUntil(eta), daysUntil(ft ?? undefined));

  const vms = [
    { id: 'SHP-2607-01', dbId: '1', displayName: 'Snow Peak · เติมสต๊อก', mode: 'sea',
      phase: 'Customs', urgency: 'urgent', clock: clockOf(day(2), day(1)),
      why: 'Form JTEPA ยังไม่ยืนยัน + free time เหลือ 1 วัน',
      raw: { status: 'docs_received', eta: day(2), etd: day(-20), free_time_ends: day(1),
        storage_per_day: 1500, clearance: 'self', forwarder: 'Yusen', updated_at: iso(-1) },
      consignments: [{ id: 'c1', supplier_name: 'Snow Peak' }],
      requirements: [R('c1', 'bl', 'received', 'before_pickup'),
        R('c1', 'certificate_of_origin', 'missing', 'before_clear'),
        R('c1', 'packing_list', 'flagged', 'before_clear'), R(null, 'goods_receipt', 'missing', 'on_arrival')] },
    { id: 'SHP-2607-02', dbId: '2', displayName: 'Nalgene', mode: 'sea',
      phase: 'Docs & Form', urgency: 'watch', clock: clockOf(day(3), null),
      why: 'รอ CO — ใกล้ถึงแล้ว (3 วัน)',
      raw: { status: 'docs_received', eta: day(3), etd: day(-25), free_time_ends: null,
        clearance: 'broker', forwarder: 'TKL', updated_at: iso(-1) },
      consignments: [{ id: 'c2', supplier_name: 'Nalgene' }],
      requirements: [R('c2', 'bl', 'received', 'before_pickup'),
        R('c2', 'certificate_of_origin', 'received', 'before_clear'), R(null, 'goods_receipt', 'missing', 'on_arrival')] },
    { id: 'SHP-2608-01', dbId: '3', displayName: 'Klymit', mode: 'air',
      phase: 'Order & Pay', urgency: 'ok', clock: clockOf(day(18), null),
      why: 'ยังเร็ว กำลังรวบรวมเอกสาร',
      raw: { status: 'ordered', eta: day(18), etd: day(10), free_time_ends: null,
        clearance: 'broker', forwarder: 'TKL', updated_at: iso(-1) },
      consignments: [{ id: 'c3', supplier_name: 'Klymit' }], requirements: [] },
    { id: 'SHP-2606-03', dbId: '4', displayName: 'DOD Outdoors', mode: 'sea',
      phase: 'Customs', urgency: 'ok', clock: clockOf(day(-6), null),
      why: 'ผ่านพิธีแล้ว รอตรวจรับเข้าคลัง',
      raw: { status: 'customs_submitted', eta: day(-6), etd: day(-30), free_time_ends: day(-1),
        clearance: 'broker', forwarder: 'TKL', updated_at: iso(-4) },
      consignments: [{ id: 'c4', supplier_name: 'DOD Outdoors' }],
      requirements: [R(null, 'goods_receipt', 'missing', 'on_arrival')] },
    { id: 'SHP-2606-01', dbId: '5', displayName: 'Barebones', mode: 'sea',
      phase: 'Order & Pay', urgency: 'ok', clock: Infinity,
      why: 'ค้างที่ชำระเงิน — ไม่ขยับนาน',
      raw: { status: 'paid', eta: null, etd: null, free_time_ends: null,
        clearance: 'broker', forwarder: null, updated_at: iso(-20) },
      consignments: [{ id: 'c5', supplier_name: 'Barebones' }],
      requirements: [R('c5', 'commercial_invoice', 'missing', 'before_ship', true)]
        .map(r => ({ ...r, updated_at: iso(-20) })) },
  ];
  const certs = [{ consignment_id: 'c1', verified: false, form_type: 'AJ_JTEPA' },
    { consignment_id: 'c2', verified: false, form_type: 'D' }];
  return buildToday(vms, certs, now);
}

export function mockReminders(now = new Date()) {
  const p2 = (n) => String(n).padStart(2, '0');
  const day = (off) => { const d = new Date(now); d.setDate(d.getDate() + off); return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`; };
  return [
    { id: 'm1', title: 'โทรตาม Form JTEPA จาก Snow Peak', note: 'ยังไม่ส่งมา 2 วันแล้ว', due_date: day(0), due_time: '10:00', done: false, shipment_id: 'SHP-2607-01', assignee: 'u1', created_by: 'u1' },
    { id: 'm2', title: 'ส่งจดหมายแลก D/O ให้ Yusen', note: null, due_date: day(1), due_time: null, done: false, shipment_id: null, assignee: 'u2', created_by: 'u1' },
    { id: 'm3', title: 'เตรียมเงินค่าอากรตู้ Nalgene', note: 'ประมาณ ฿45,000', due_date: day(4), due_time: null, done: false, shipment_id: null, assignee: null, created_by: 'u1' },
    { id: 'm4', title: 'เช็คราคาค่าเรือเดือนหน้า', note: null, due_date: null, due_time: null, done: false, shipment_id: null, assignee: null, created_by: 'u2' },
    { id: 'm5', title: 'ยืนยันนัดตรวจของกับคลัง', note: null, due_date: day(-1), due_time: null, done: true, shipment_id: null, assignee: 'u1', created_by: 'u1' },
  ];
}

export function buildToday(vms, certs = [], now = new Date()) {
  const certById = {};
  for (const c of certs) certById[c.consignment_id] = c;   // 1 CO ต่อ consignment
  const active = vms.filter(v => isOpen(v.raw?.status));

  const urgent = buildUrgent(active);
  const tasks = buildTasks(active, certById);
  const calendar = buildCalendar(vms);
  const backlog = buildBacklog(active, now);
  const unscheduled = active.filter(v => !v.raw?.eta);   // active ที่ยังไม่กำหนด ETA (ไม่ลงปฏิทิน)

  const taskTotal = tasks.reduce((n, g) => n + g.items.length, 0);
  return {
    urgent, tasks, calendar, backlog, unscheduled,
    counts: {
      active: active.length,
      urgent: urgent.length,
      tasks: taskTotal,
      backlog: backlog.length,
    },
  };
}
